import { PageHead } from "../GtaxShell";
import { GtaxTraded } from "../GtaxTraded";
import { Prose } from "../GtaxSections";

const PILLARS = [
  ["I", "Session structure", "Activity concentrates on the London and New York sessions, when gold's liquidity is deepest and price discovery is most reliable."],
  ["II", "Macro awareness", "Positioning is framed around the US dollar, real yields and scheduled macro events. Exposure is reduced into high-impact releases."],
  ["III", "Intraday execution", "Short-horizon, two-way trading. The desk can be long or short, and seeks to capture the day's range rather than forecast the year."],
  ["IV", "Risk first", "Position sizing is set relative to treasury equity, with a daily loss limit after which the desk stops trading for the day."],
];

const RISK = [
  ["Daily loss limit", "Trading halts for the day once the limit is reached"],
  ["Position sizing", "Calibrated to consolidated treasury equity, never to targets"],
  ["Event risk", "Exposure reduced ahead of tier-one macro releases"],
  ["Weekend risk", "Trading Monday to Friday only"],
];

export default function GtaxStrategy() {
  return (
    <div data-testid="gtax-strategy-page">
      <PageHead eyebrow="Our strategy" title="A disciplined, discretionary gold mandate."
        sub="One instrument, two sessions, four principles. The strategy seeks intraday opportunity in XAU/USD within defined risk limits. Losses can and do occur."
        img="/gtax/chart.jpg" />
      <section className="py-24 gx-line">
        <div className="gx-wrap grid md:grid-cols-2 gap-6">
          {PILLARS.map(([n, h, p]) => (
            <div key={n} className="gx-card p-10 grid grid-cols-[56px_1fr] gap-6" data-testid={`gtax-pillar-${n}`}>
              <div className="gx-serif text-4xl gx-gold">{n}</div>
              <div><h3 className="gx-caps text-xl">{h}</h3><p className="mt-3 text-[color:var(--mute)] leading-relaxed">{p}</p></div>
            </div>
          ))}
        </div>
      </section>
      <Prose id="process" eyebrow="Investment process" title="From market context to closed trade, every day follows the same process." paras={[
        "Each trading day begins with a review of the macro calendar, the US dollar, real yields and overnight developments. High-impact US releases — inflation, employment, central-bank decisions — are marked in advance, and the desk reduces or closes exposure in the period around them.",
        "During the London and New York sessions the desk trades XAU/USD intraday, long or short. Trades are sized relative to consolidated treasury equity, each with a defined risk, and the desk aims to capture moves within the day rather than to forecast the long-term price of gold.",
        "Positions are generally closed within the session. Results are recorded daily in the member ledger. Losing days happen and are part of the process; the daily loss limit exists so that no single day can change the character of the mandate.",
      ]} />
      <Prose id="not" eyebrow="What we do not do" title="Boundaries are part of the strategy." ivory paras={[
        "We do not trade any instrument other than gold. We do not trade at weekends. We do not increase position size to recover losses, and we do not average into losing positions without a defined risk.",
        "We do not promise, target or advertise any fixed, minimum or periodic return. Distributions are paid only from realised net trading profit. On days, weeks or months without realised profit, no distribution is made.",
        "We do not hold member funds outside the GTA X Consolidated Treasury, and we never ask members to send funds to personal addresses or through intermediaries. Treasury details are shared only privately, after approval.",
      ]} />
      <GtaxTraded />
      <section className="py-24 gx-line gx-ivory">
        <div className="gx-wrap grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <p className="gx-eyebrow">Risk governance</p>
            <h2 className="gx-serif text-4xl md:text-5xl mt-6">Capital preservation is the first trade.</h2>
            <dl className="mt-10 divide-y divide-[color:var(--line)] border-y border-[color:var(--line)]">
              {RISK.map(([k, v]) => (
                <div key={k} className="grid grid-cols-[180px_1fr] gap-6 py-5"><dt className="gx-caps text-sm">{k}</dt><dd className="text-[color:var(--mute)]">{v}</dd></div>
              ))}
            </dl>
          </div>
          <div className="lg:col-span-6 gx-photo h-[420px]"><img src="/gtax/coins.jpg" alt="Gold coins and balance scale" /></div>
        </div>
      </section>
    </div>
  );
}
